// Utilitaires pour traiter les scores dans les données d'analyse des podcasts

/**
 * Extrait les 2 éléments avec les meilleurs scores parmi toutes les sections d'un épisode
 * @param {Object} episodeData - Les données d'analyse d'un épisode
 * @returns {Array} - Array des 2 meilleurs éléments avec leurs scores
 */
export const getBestScoredContent = (episodeData) => {
  if (!episodeData || typeof episodeData !== 'object') return []
  
  const allScoredItems = []
  
  // Sections typiques qui peuvent contenir des scores
  const scoredSections = [
    'intro_news',
    'annoyance_stories', 
    'farfie_moments',
    'games',
    'notable_moments',
    'discussion_points',
    'culture_recommendations',
    'moments_notables'
  ]
  
  // Parcourir toutes les sections possibles
  scoredSections.forEach(sectionName => {
    const section = episodeData[sectionName]
    if (Array.isArray(section)) {
      section.forEach(item => {
        if (item && typeof item === 'object' && typeof item.score === 'number') {
          allScoredItems.push({
            content: item.content || '',
            timestamp: item.timestamp || null,
            score: item.score,
            section: sectionName,
            sectionDisplayName: getSectionDisplayName(sectionName)
          })
        }
      })
    }
  })
  
  // Trier par score décroissant et prendre les 2 meilleurs
  return allScoredItems
    .sort((a, b) => b.score - a.score)
    .slice(0, 2)
}

/**
 * Obtient le nom d'affichage d'une section
 * @param {string} sectionName - Le nom technique de la section
 * @returns {string} - Le nom d'affichage de la section
 */
const getSectionDisplayName = (sectionName) => {
  const displayNames = {
    'intro_news': 'Actualités',
    'annoyance_stories': 'Histoires d\'énervement',
    'farfie_moments': 'Moments farssissages',
    'games': 'Jeux',
    'notable_moments': 'Moments notables',
    'discussion_points': 'Points de discussion',
    'culture_recommendations': 'Recommandations culturelles',
    'moments_notables': 'Moments notables'
  }
  
  return displayNames[sectionName] || sectionName
}

/**
 * Vérifie si un épisode a des contenus avec scores
 * @param {Object} episodeData - Les données d'analyse d'un épisode
 * @returns {boolean} - True si l'épisode a des contenus avec scores
 */
export const hasScores = (episodeData) => {
  const bestContent = getBestScoredContent(episodeData)
  return bestContent.length > 0
}

/**
 * Obtient le score moyen d'un épisode
 * @param {Object} episodeData - Les données d'analyse d'un épisode
 * @returns {number} - Le score moyen ou 0 si pas de scores
 */
export const getAverageScore = (episodeData) => {
  if (!episodeData || typeof episodeData !== 'object') return 0
  
  const allScoredItems = []
  
  const scoredSections = [
    'intro_news',
    'annoyance_stories', 
    'farfie_moments',
    'games',
    'notable_moments',
    'discussion_points',
    'culture_recommendations',
    'moments_notables'
  ]
  
  scoredSections.forEach(sectionName => {
    const section = episodeData[sectionName]
    if (Array.isArray(section)) {
      section.forEach(item => {
        if (item && typeof item === 'object' && typeof item.score === 'number') {
          allScoredItems.push(item.score)
        }
      })
    }
  })
  
  if (allScoredItems.length === 0) return 0
  
  const sum = allScoredItems.reduce((acc, score) => acc + score, 0)
  return Math.round((sum / allScoredItems.length) * 10) / 10 // Arrondir à 1 décimale
}